import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import Drawer from '@mui/material/Drawer';
import { Logo } from 'components';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { ClientRoute } from 'routing/Routes';
import { getTopLevelPath, normalizePath, pathMatch } from 'utility';
import { useLanguage } from 'components/util/Language';
import { SterlingIcon, IconName } from 'components/general/SterlingIcon';
import { useNavStyles, NavDiv } from './styled.Nav';

// List of special resource routes that need special handling
const RESOURCE_ROUTES = [
  'standards',
  'program-standards-2021',
  'support',
  'changelog',
  'recognition',
  'preferred-vendors',
  'resources',
  'education-materials',
  'latest-updates',
];

export function MainNav(props: { routes: ClientRoute[]; permittedRoutes: string[] }) {
  const { routes: propRoutes, permittedRoutes } = props;
  const classes = useNavStyles();
  const location = useLocation();
  const topPath = getTopLevelPath(location.pathname);
  const lang = useLanguage();
  const [open, setOpen] = useState(topPath === '/');

  const selectedRoute = propRoutes.find((route) =>
    pathMatch(topPath, getTopLevelPath(route.path), true)
  );

  const forcedNavOrder = [
    /\/admin/g,
    /\/program-updates/g,
    /\/management-report/g,
    /\/progress-report/g,
    /\/performance-dashboard/g,
    /\/standards/g,
    /\/program-standards-2021/g,
    /\/preferred-vendors/g,
    /\/education/g,
    /\/ud-marketplace/g,
    /\/changelog/g,
    /\/support/g,
  ];

  // Sort routes by forced order
  // This is do to wordpress routes combining with platform routes and having their own order
  const routes = propRoutes
    .filter((route) => route.path !== '/' && !route.path.includes('/notifications'))
    .map((route) => {
      let sortedRoute: boolean | ClientRoute = false;
      forcedNavOrder.forEach((routeName, newOrder) => {
        if (routeName.test(route.path)) {
          sortedRoute = { ...route, order: newOrder };
        }
      });
      return sortedRoute || route;
    })
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    setOpen(!!(topPath === '/' || (selectedRoute && selectedRoute.children.length === 0)));
  }, [location.pathname, selectedRoute, topPath]);

  if (!selectedRoute) return <Navigate to="/" />;

  return (
    <Drawer
        variant="permanent"
        sx={{width: 'auto'}}
        classes={{
          paper: clsx({
            [classes.drawerOpenMain]: open,
            [classes.drawerCloseMain]: !open,
            [classes.drawer]: true,
          }),
        }}
        open={open}
      >
        <Logo invis={!open} logoType="main" />
        <NavDiv className={classes.linkWrapper}>
          {routes.filter((r) => !r.hideNavLink).map((route, index) => {
            const isPermitted = route.externalUrl
              ? permittedRoutes.includes(route.externalUrl)
              : permittedRoutes.some((permittedRoute) =>
                  pathMatch(permittedRoute, getTopLevelPath(route.path), true)
                );

            if (!isPermitted) {
              return null;
            }

            const highlighted = location.pathname.includes(route.path);
            const translatedTitle =
              lang === 'FR' && route.titleFr ? route.titleFr : route.title;

            const permittedChildren = route.children.filter((childRoute) =>
              permittedRoutes.some((r) => pathMatch(r, childRoute.path, true))
            );

            // Check if this is a resource route
            const routePath = normalizePath(route.path);
            const isResourceRoute = RESOURCE_ROUTES.includes(routePath);

            // Construct the path differently for resource routes
            let pathWhenClicked;

            if (isResourceRoute) {
              pathWhenClicked = route.noContent && route.children?.length > 0 && permittedChildren?.[0]
                ? lang === 'FR'
                  ? `/fr/${normalizePath(permittedChildren[0].path)}/`
                  : `/${normalizePath(permittedChildren[0].path)}/`
                : lang === 'FR'
                  ? `/fr/${routePath}/`
                  : `/${routePath}/`;
            } else {
              pathWhenClicked = route.noContent && route.children?.length > 0 && permittedChildren?.[0]
                ? lang === 'FR'
                  ? `/fr/${normalizePath(permittedChildren[0].path)}/`
                  : `/${normalizePath(permittedChildren[0].path)}/`
                : lang === 'FR'
                  ? `/fr/${routePath}/`
                  : `/${routePath}/`;
            }

            if (route.externalUrl) {
              return (
                <a
                  key={`nav-${index}`}
                  href={route.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={clsx('nav-link', !open && 'nav-shrink')}
                >
                  <span className="nav-icon">
                    <SterlingIcon name={route.itemtype as IconName} title={translatedTitle} />
                  </span>
                  <p title={translatedTitle} className={clsx('nav-text')}>
                    {translatedTitle}
                  </p>
                </a>
              );
            }

            return (
              <Link
                key={`nav-${index}`}
                to={pathWhenClicked}
                className={clsx(
                  'nav-link',
                  highlighted && 'nav-selected',
                  !open && 'nav-shrink'
                )}
              >
                <span className={clsx(highlighted && 'nav-selected', 'nav-icon')}>
                  <SterlingIcon name={route.itemtype as IconName} title={translatedTitle} />
                </span>
                <p title={translatedTitle} className={clsx('nav-text')}>
                  {translatedTitle}
                </p>
              </Link>
            );
          })}
        </NavDiv>
      </Drawer>
  );
}
